<template>
  <div
    :class="classes"
    :data-qa="dataQa"
    @mouseenter="hovering = true"
    @mouseleave="hovering = false">
    <div
      v-if="$slots.prepend"
      class="s-input-group__prepend"
      :style="dopStyle && dopStyle.prepend ? dopStyle.prepend : ''"
      @click="$emit('click-prepend')">
      <!-- @slot содержимое, добавляемое перед вводом, работает только при типе 'text' -->
      <slot name="prepend"/>
    </div>
    <span
      v-if="readonly || disabled"
      :class="{ 's-input__inner': readonly || disabled }"
      :data-qa="`${dataQa}-input`">{{
        amount
      }}</span>
    <input
      v-else
      ref="numeric"
      v-model="amount"
      class="s-input__inner"
      :class="`is-${size}`"
      :data-qa="`${dataQa}-input`"
      :disabled="inputDisabled"
      :name="`${dataQa}-input`"
      :placeholder="placeholder"
      type="tel"
      @blur="onBlurHandler"
      @change="onChangeHandler"
      @focus="onFocusHandler"
      @input="onInputHandler"
      @keydown.enter="$emit('keydown_enter')"
      @keypress="onlyForCurrency">
    <span
      v-if="showClear"
      class="s-input__suffix"
      :data-qa="`${dataQa}-suffix`">
      <span class="s-input__suffix-inner">
        <sButton
          class="s-input__clear"
          icon="cross"
          small
          type="text"
          @click.stop="clear"
          @mousedown.prevent/>
      </span>
    </span>
    <div
      v-if="$slots.append"
      class="s-input-group__append"
      @click="$emit('click-append')">
      <!-- @slot контент, добавляемый после ввода, работает только при типе 'text' -->
      <slot name="append"/>
    </div>
  </div>
</template>

<script>
import Form from '../mixins/form.js'
import emitter from '../mixins/emitter'
import accounting from 'accounting-js'

/**
 * Ввод цифр и сумм
 * @displayName InputNumber
 */
export default {
  name: 'sInputNumber',
  componentName: 'sInputNumber',
  mixins: [emitter, Form],
  props: {
    /**
     * Значение
     */
    value: {
      type: [Number, String],
    },
    /**
     * Максимально допустимое значение
     */
    max: {
      type: Number,
      default: Number.MAX_SAFE_INTEGER || 9007199254740991,
    },
    /**
     * Минимальное допустимое значение
     */
    min: {
      type: Number,
      default: Number.MIN_SAFE_INTEGER || -9007199254740991,
    },
    /**
     * Включение/отключение отрицательного значения
     */
    minus: {
      type: Boolean,
      default: false,
    },
    /**
     * placeholder
     */
    placeholder: {
      type: String,
      default: '',
    },
    /**
     * Значение, когда ввод пуст
     */
    emptyValue: {
      type: [Number, String],
      default: 0,
    },
    /**
     * Количество знаков после запятой
     * Символ десятичных знаков противоположен символу разделителя.
     */
    precision: {
      type: Number,
      default: 2,
      validator: function (value) {
        return value >= 0 && value <= 5
      }
    },
    /**
     * Использовать разделитель тысяч
     */
    useThousandSeparator: {
      type: Boolean,
      default: true,
    },
    /**
     * Разделитель
     */
    separator: {
      type: String,
      default: '.',
      validator: function (value) {
        return ['.', ','].indexOf(value) !== -1
      }
    },
    /**
     * Тип вывода, используемый для модели.
     */
    outputType: {
      type: String,
      default: 'Number',
      validator: function (value) {
        return ['String', 'Number'].indexOf(value) !== -1
      }
    },
    /**
     * Скрыть ввод и показать значение только в тексте.
     */
    readonly: {
      type: Boolean,
      default: false,
    },
    /**
     * Признак блокировки
     */
    disabled: {
      type: Boolean,
    },
    /**
     * Символ валюты
     */
    currency: {
      type: String,
      default: '',
    },
    /**
     * Положение символа валюты
     */
    currencySymbolPosition: {
      type: String,
      default: 'suffix',
      validator: function (value) {
        return ['suffix', 'prefix'].indexOf(value) !== -1
      }
    },
    /**
     * Возможность очистить данные через кнопку
     */
    clearable: {
      type: Boolean,
      default: false
    },
    /**
     * Принудительное отображение кнопки очистки. Игнорирует признак `clearable` и `readonly` и `disabled`
     */
    clearForcible: {
      type: Boolean,
      default: false
    },
    /**
     * Ключ для автотестов
     * @ignore
     */
    dataQa: {
      type: String,
      default: 'input'
    },
    /**
     * запускать ли проверку формы. При использовании FormItem
     */
    validateEvent: {
      type: Boolean,
      default: true
    },
    /**
     * Доп стили (объект. Структуру будем дополнять по необходимости)
     */
    dopStyle: {
      type: Object
    },
    /**
     * Разрешить отображение и использование числа 0
     */
    allowZero: {
      type: Boolean,
      default: false
    },
    size: {
      type: String,
      default: 'normal',
      validator: function (value) {
        const correct = ['small', 'normal'].indexOf(value) !== -1
        return correct
      },
    },
  },
  data() {
    return {
      amount: '',
      hovering: false,
      focused: false,
    }
  },
  computed: {
    /**
     * Проверяем, было ли значение намеренно установлено равным нулю
     * @return {boolean}
     */
    isDeliberatelyZero() {
      return this.valueNumber === 0 && this.value !== '' && this.allowZero
    },
    classes() {
      let result = [
        this.$attrs.className,
        's-input',
        {
          'is-disabled': this.inputDisabled,
          'is-readonly': this.readonly,
        }
      ]
      if (this.$slots.prepend || this.$slots.append) {
        result.push('s-input-group')
      }
      if (this.$slots.append) {
        result.push('s-input-group--append')
      }
      if (this.$slots.prepend) {
        result.push('s-input-group--prepend')
      }
      if (this.clearable) {
        result.push('s-input--suffix')
      }
      return result
    },
    /**
     * Числовой тип форматированного значения
     * @return {Number}
     */
    amountNumber() {
      return this.unformat(this.amount)
    },
    /**
     * Числовой тип свойства значения
     * @return {Number}
     */
    valueNumber() {
      return this.unformat(this.value)
    },
    /**
     * Определите позицию формата для символа валюты и значения.
     * @return {String} format
     */
    symbolPosition() {
      if (!this.currency) {
        return '%v'
      }
      return this.currencySymbolPosition === 'suffix' ? '%v %s' : '%s %v'
    },
    showClear() {
      // Проверяем, можно ли использовать кнопку очистки
      const canUseClear = this.clearForcible || (this.clearable && !this.inputDisabled && !this.readonly)

      // Проверяем, является ли значение допустимым для отображения
      const isValidValue = this.value !== undefined && (
        !!this.value || (this.allowZero && this.value === 0)
      )

      // Проверяем, активно ли поле (фокус или наведение)
      const isActive = this.focused || this.hovering

      // Возвращаем результат на основе всех условий
      return canUseClear && isValidValue && isActive
    },
  },
  watch: {
    valueNumber(newValue) {
      if (this.$refs.numeric !== document.activeElement) {
        this.amount = this.format(newValue)

        if (this.validateEvent) {
          this.dispatch('sFormItem', 's.form.change', [newValue])
        }
      }
    },
    useThousandSeparator() {
      this.process(this.valueNumber)
      this.amount = this.format(this.valueNumber)
    },
    separator() {
      this.process(this.valueNumber)
      this.amount = this.format(this.valueNumber)
    },
    /**
     * Немедленно отразите изменения валюты
     */
    currency() {
      this.process(this.valueNumber)
      this.amount = this.format(this.valueNumber)
    },
    /**
     * Немедленно отразите изменения точности
     */
    precision() {
      this.process(this.valueNumber)
      this.amount = this.format(this.valueNumber)
    }
  },
  mounted() {
    // Установливаем свойства значения по умолчанию, когда valueNumber имеет какое-то значение
    if (this.valueNumber || this.isDeliberatelyZero) {
      this.process(this.valueNumber)
      this.amount = this.format(this.valueNumber)
      // In case of delayed props value.
      setTimeout(() => {
        this.process(this.valueNumber)
        this.amount = this.format(this.valueNumber)
      }, 500)
    }
  },
  methods: {
    clear() {
      if (this.inputDisabled) { return }
      this.update(this.min === Number.MIN_SAFE_INTEGER || -9007199254740991 ? this.emptyValue : this.min)
      /**
      * Данные были очищены
      * @event clear
      */
      this.$emit('clear')
    },
    onlyForCurrency($event) {
      if (this.inputDisabled) { return }
      let keyCode = ($event.keyCode ? $event.keyCode : $event.which)

      if ($event.keyCode === 13) {
        this.$emit('enter', this.amountNumber)
      }

      let correct = false
      const amount = (this.amountNumber || 0).toString()
      if (keyCode >= 48 && keyCode <= 57) {
        /* Только цифры */
        correct = true

      } else if (this.precision !== 0 && String.fromCharCode(keyCode) === this.separator) {
        /* Если разрешены десятичные, то пропускаем разделитель, если его еще не ввели */
        correct = !amount || (amount && amount.indexOf(this.separator) == -1)
      } else if (this.minus && keyCode === 45) {
        /* Если разрешено значение меньше нуля, то пропускаем минус, если его еще не ввели */
        correct = !amount || (amount && amount.indexOf('-') == -1)
      }

      if (!correct) {
        $event.preventDefault()
      }
    },
    /**
     * Обработка события изменения
     * @param {Object} e
     */
    onChangeHandler(e) {
      if (this.inputDisabled) { return }
      this.$emit('change', e)
    },
    /**
     * Обработка события размытия
     * @param {Object} e
     */
    onBlurHandler(e) {
      if (this.inputDisabled) { return }
      this.focused = false
      this.$emit('blur', e)
      this.amount = this.format(this.valueNumber)
      if (this.validateEvent) {
        this.dispatch('sFormItem', 's.form.blur', [this.text])
      }
    },
    /**
     * Обработка события фокуса
     * @param {Object} e
     */
    onFocusHandler(e) {
      if (this.inputDisabled) { return }
      this.$emit('focus', e)
      this.focused = true
      if (this.valueNumber === 0 && !this.allowZero) {
        this.amount = null
      } else {
        this.amount = this.valueNumber
      }
    },
    /**
     * Обработка события ввода
     */
    onInputHandler() {
      this.process(this.amountNumber)
    },
    /**
     * Проверка значение перед обновлением компонента.
     * @param {Number} value
     */
    process(value) {
      if (this.inputDisabled) { return }
      if (value >= this.max) {
        this.update(this.max)
      }
      if (value <= this.min && !(value === 0 && this.allowZero)) {
        this.update(this.min)
      }
      if (value > this.min && value < this.max) {
        this.update(value)
      }
      if (!this.minus && value < 0) {
        this.min >= 0 ? this.update(this.min) : this.update(0)
      }
    },
    /**
     * Обновление значения модели родительского компонента.
     * @param {Number} value
     */
    update(value) {
      if (this.inputDisabled) { return }
      let output
      if (value !== null || (value === 0 && this.allowZero)) {
        const fixedValue = accounting.toFixed(value, this.precision)
        output = this.outputType.toLowerCase() === 'string' ? fixedValue : Number(fixedValue)
      }
      this.$emit('update:value', output)
    },
    toFixedIfNecessary(value, decimal) {
      return +parseFloat(value).toFixed( decimal )
    },
    /**
     * Форматируем значение с помощью символа и разделителя.
     * @param {Number} value
     * @return {String}
     */
    format(value) {
      const resultInt = this.toFixedIfNecessary(value, Number(this.precision))

      let deciamal = 0
      let result = ''
      if (resultInt || (resultInt === 0 && this.allowZero)) {
        const valuePrecision = resultInt.toString().split('.')[1]?.length || 0
        deciamal = valuePrecision < Number(this.precision) ? valuePrecision : Number(this.precision)
        result = accounting.formatNumber(resultInt, {
          precision: deciamal,
          decimal: this.separator,
          thousand: this.useThousandSeparator ? ' ' : ''
        })
      }
      if (this.currency) {
        result = this.currencySymbolPosition === 'suffix' ? `${result} ${this.currency}` : `${this.currency} ${result}`
      }

      return result
      /*return accounting.formatMoney(value, {
        symbol: this.currency,
        format: this.symbolPosition,
        precision: Number(this.precision),
        decimal: this.separator,
        thousand: this.useThousandSeparator ? ' ' : ''
      })*/
    },
    /**
     * Удаление символ и разделитель.
     * @param {Number} value
     * @return {Number}
     */
    unformat(value) {
      if (value == null) {
        return null
      }
      const toUnformat = typeof value === 'string' && value === '' ? this.emptyValue : value
      return accounting.unformat(toUnformat, this.separator)
    },
  }
}
</script>
